import { useTranslation } from 'react-i18next';
import { Button } from '@xxtract/ui';

import { formatFileSize } from '@/lib/formatters';
import type { ConvertFormat, ConvertSize } from '../media.api';
import { ConvertOptions } from './ConvertOptions';

/** Pictogramnaam, geen tekst voor de gebruiker. */
const SPINNER_ICON = 'progress_activity';

export interface MediaBulkBarProps {
  selectedCount: number;
  totalSizeBytes: number;
  onDownloadZip: () => void;
  onDeselect: () => void;
  isDownloading?: boolean;
  /** Conversion controls (h11-3, mockup dam.html bulkbar "Converteren: PNG ▾ · Afmeting: ..."). */
  convertFormat: ConvertFormat | null;
  onConvertFormatChange: (value: ConvertFormat | null) => void;
  convertSize: ConvertSize;
  onConvertSizeChange: (value: ConvertSize) => void;
  /** Count of documents (e.g. PDFs) in the selection, for the "N included unchanged" notice. */
  documentCount: number;
}

/**
 * Sticky bulk-action bar for the DAM library (h11-2, mockup dam.html `.bulkbar`), shown
 * only while one or more files are selected. Same pattern as the invoices bulk bar
 * (`InvoiceBulkBar`) and the product-media page's selection actions. Includes the h11-3
 * conversion controls, since the mockup places them inside the same bar.
 */
export function MediaBulkBar({
  selectedCount, totalSizeBytes, onDownloadZip, onDeselect, isDownloading = false,
  convertFormat, onConvertFormatChange, convertSize, onConvertSizeChange, documentCount,
}: MediaBulkBarProps) {
  const { t } = useTranslation();

  if (selectedCount === 0) return null;

  return (
    <div
      className="sticky bottom-4 flex flex-wrap items-center gap-3 rounded-xl border border-[var(--border)] bg-[var(--background)] px-4 py-3 shadow-[var(--card-shadow)]"
      data-testid="media-bulk-bar"
      role="toolbar"
      aria-label={t('media.library.bulkBar.label')}
    >
      <span className="text-sm font-semibold text-[var(--foreground)]">
        {t('media.library.bulkBar.selectedCount', { count: selectedCount, size: formatFileSize(totalSizeBytes) })}
      </span>
      <Button disabled={isDownloading} onClick={onDownloadZip} size="sm" variant="secondary">
        {isDownloading ? (
          <>
            {/* Uitgeschakeld alleen leest als "kapot"; dit laat zien dat hij bezig is. */}
            <span aria-hidden="true" className="material-symbols-outlined animate-spin text-[16px]" data-testid="media-bulk-downloading">{SPINNER_ICON}</span>
            {t('media.library.bulkBar.downloadingZip')}
          </>
        ) : (
          <>
            <span aria-hidden="true" className="material-symbols-outlined text-[16px]">folder_zip</span>
            {t('media.library.bulkBar.downloadZip')}
          </>
        )}
      </Button>
      <ConvertOptions
        documentCount={documentCount}
        format={convertFormat}
        onFormatChange={onConvertFormatChange}
        onSizeChange={onConvertSizeChange}
        size={convertSize}
      />
      <Button className="ml-auto" onClick={onDeselect} size="sm" variant="ghost">
        {t('media.library.bulkBar.deselect')}
      </Button>
    </div>
  );
}
